import { Pencil, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

type PlanNameEditorProps = {
  isEditing: boolean;
  displayedTitle: string;
  nameDraft: string;
  isSaving: boolean;
  onStartEditing: () => void;
  onDraftChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
};

/**
 * Editable plan title block at the top of the FP results page. View mode
 * shows the resolved title (custom name -> care stage label) plus a pencil
 * affordance; edit mode swaps in an input with Save / Cancel buttons and
 * Enter / Escape keyboard handling.
 */
export const PlanNameEditor = ({
  isEditing,
  displayedTitle,
  nameDraft,
  isSaving,
  onStartEditing,
  onDraftChange,
  onSave,
  onCancel,
}: PlanNameEditorProps) => {
  if (!isEditing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-termina text-2xl font-medium text-text-primary">
          {displayedTitle}
        </h1>
        <button
          type="button"
          onClick={onStartEditing}
          aria-label="Rename care guide"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-tertiary transition-colors duration-150 ease-out hover:bg-card-subtle hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        autoFocus
        type="text"
        value={nameDraft}
        maxLength={200}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Name this care guide"
        className="min-w-[260px] flex-1 max-w-md rounded-md border border-border-subtle bg-white px-3 py-1.5 font-termina text-2xl font-medium text-text-primary focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage"
      />
      <Button
        type="button"
        size="sm"
        onClick={onSave}
        disabled={isSaving}
        className="h-8 bg-[#026857] text-white hover:bg-[#025247]"
      >
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onCancel}
        disabled={isSaving}
        className="h-8"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  );
};
